'use client';

import { useEffect, useRef, useState } from 'react';
import { MapPin, GraduationCap, Coffee, Code2, Zap, Target, Users, Award } from 'lucide-react';
import { PORTFOLIO_STATS } from '@/data/portfolioData';
import SectionHeader from './SectionHeader';

const STATS = [
  { value: `${PORTFOLIO_STATS.projects}`, label: 'Projects built', icon: Target, accent: 'text-sand' },
  { value: `${PORTFOLIO_STATS.certifications}`, label: 'Certifications', icon: Award, accent: 'text-clay' },
  { value: `${PORTFOLIO_STATS.technologies}+`, label: 'Technologies', icon: Zap, accent: 'text-sage' },
  { value: `${PORTFOLIO_STATS.skillAreas}`, label: 'Skill areas', icon: Code2, accent: 'text-rose' },
];

const HIGHLIGHTS = [
  {
    icon: GraduationCap,
    title: 'Computer Science Student',
    desc: 'Faculty of Computers and AI — Expected 2027',
  },
  {
    icon: MapPin,
    title: 'Based in Egypt',
    desc: 'Giza — Open to remote worldwide',
  },
  {
    icon: Coffee,
    title: 'Passionate Builder',
    desc: 'Turning ideas into production-ready apps',
  },
  {
    icon: Users,
    title: 'Collaborative',
    desc: 'Team player with clear communication',
  },
];

function AnimatedCounter({ target, suffix = '' }: { target: string; suffix?: string }) {
  const [displayed, setDisplayed] = useState('0');
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          const numericTarget = parseInt(target.replace(/\D/g, ''), 10);
          if (isNaN(numericTarget)) {
            setDisplayed(target);
            return;
          }
          let current = 0;
          const step = Math.ceil(numericTarget / 30);
          const interval = setInterval(() => {
            current = Math.min(current + step, numericTarget);
            setDisplayed(`${current}`);
            if (current >= numericTarget) clearInterval(interval);
          }, 40);
        }
      },
      { threshold: 0.5 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, started]);

  return (
    <span ref={ref} className="font-mono-data">
      {displayed}
      {suffix}
    </span>
  );
}

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const reveal = (delay: number) =>
    `transition-all duration-800 ease-organic ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
    }`;
  const revealStyle = (delay: number) => ({ transitionDelay: `${delay}ms` });

  return (
    <section id="about" ref={sectionRef} className="py-28 xl:py-40 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        <div className={reveal(0)} style={revealStyle(0)}>
          <SectionHeader index="01 — About" title="Who I" highlight="am" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-20">
          {/* Bio — wider asymmetric column */}
          <div className={`lg:col-span-7 ${reveal(100)}`} style={revealStyle(100)}>
            <div className="warm-card rounded-2xl p-8 xl:p-10 mb-8">
              <p className="font-mono text-xs text-sand tracking-widest uppercase mb-6">The story</p>

              <div className="space-y-5 text-parchment-muted text-lg leading-relaxed">
                <p>
                  I&apos;m <strong className="text-parchment font-600">Marwan Hussein</strong>, a
                  Software Engineer and Computer Science student at the Faculty of Computers and AI.
                  I specialize in backend and web development with Python, Django, and database technologies.
                </p>
                <p>
                  Hands-on with{' '}
                  <span className="text-sand font-mono text-sm">Django</span>,{' '}
                  <span className="text-sand font-mono text-sm">Python</span>,{' '}
                  <span className="text-sand font-mono text-sm">MySQL</span>, and{' '}
                  <span className="text-sand font-mono text-sm">C#/ASP.NET</span> — building real-world
                  projects from task-worker matching apps to school management systems.
                </p>
                <p>
                  Driven by clean code, thoughtful design, and the satisfaction of solving real problems.
                  Currently seeking opportunities to grow and contribute to meaningful work.
                </p>
              </div>

              <dl className="mt-10 pt-8 border-t border-parchment/8 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { label: 'Name', value: 'Marwan Hussein' },
                  { label: 'Location', value: 'Giza, Egypt' },
                  { label: 'Email', value: 'marwan.hussein.450@gmail.com' },
                  { label: 'Status', value: 'Open to work' },
                ].map((fact) => (
                  <div key={fact.label}>
                    <dt className="font-mono text-[10px] text-parchment-muted uppercase tracking-widest mb-1">
                      {fact.label}
                    </dt>
                    <dd className="text-parchment text-sm">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {HIGHLIGHTS.map((h, i) => {
                const Icon = h.icon;
                return (
                  <div
                    key={h.title}
                    className="warm-card warm-card-hover rounded-xl p-5"
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <Icon size={18} className="text-sand mb-3" />
                    <h3 className="font-display text-parchment text-sm font-600 mb-1">{h.title}</h3>
                    <p className="text-parchment-muted text-sm leading-relaxed">{h.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Stats + snippet — offset column */}
          <div className={`lg:col-span-5 ${reveal(200)}`} style={revealStyle(200)}>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {STATS.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="warm-card warm-card-hover rounded-xl p-5 text-center">
                    <Icon size={18} className={`${stat.accent} mx-auto mb-3`} />
                    <div className={`font-display text-3xl font-700 ${stat.accent} mb-1`}>
                      <AnimatedCounter target={stat.value.replace('+', '')} suffix="+" />
                    </div>
                    <div className="text-parchment-muted text-xs">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            <div className="warm-card rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-parchment/6">
                <div className="w-2.5 h-2.5 rounded-full bg-rose/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-sand/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-sage/60" />
                <span className="ml-2 font-mono text-[10px] text-parchment-muted">marwan.json</span>
              </div>
              <pre className="code-block p-6 text-parchment-muted overflow-x-auto">
                <code>
                  <span className="text-parchment-muted/40">{'{'}</span>
                  {'\n'}
                  {'  '}
                  <span className="text-clay">&quot;name&quot;</span>
                  <span className="text-parchment-muted/50">: </span>
                  <span className="text-sage">&quot;Marwan Hussein&quot;</span>
                  <span className="text-parchment-muted/40">,</span>
                  {'\n'}
                  {'  '}
                  <span className="text-clay">&quot;role&quot;</span>
                  <span className="text-parchment-muted/50">: </span>
                  <span className="text-sage">&quot;Software Engineer&quot;</span>
                  <span className="text-parchment-muted/40">,</span>
                  {'\n'}
                  {'  '}
                  <span className="text-clay">&quot;focus&quot;</span>
                  <span className="text-parchment-muted/50">: </span>
                  <span className="text-sage">&quot;Backend + Web&quot;</span>
                  <span className="text-parchment-muted/40">,</span>
                  {'\n'}
                  {'  '}
                  <span className="text-clay">&quot;status&quot;</span>
                  <span className="text-parchment-muted/50">: </span>
                  <span className="text-sand">&quot;open_to_work&quot;</span>
                  {'\n'}
                  <span className="text-parchment-muted/40">{'}'}</span>
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
